import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { usePeut } from '../../../auth/usePeut.js';
import { AuditTable } from '../../../components/AuditTable.js';
import { TableauDefilant } from '../../../components/TableauDefilant.js';
import { estClos } from '../../../lib/ouvertures.js';
import type { DossierOuverture } from '../../../types.js';
import pageStyles from '../../../styles/page.module.css';
import tableStyles from '../../../styles/table.module.css';
import styles from '../Ouvertures.module.css';
import { ConnecteurDropbox } from './connecteur/ConnecteurDropbox.js';

/** Direction, membres et moment des invitations. */
export function EquipeDossier({ dossier }: { dossier: DossierOuverture }): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const { equipe } = dossier;
  const modifiable =
    peut('console:manage-onboarding') && !estClos(dossier.etat) && dossier.galleryId === null;

  return (
    <div className={styles.pile}>
      <section className={pageStyles.panneau} aria-labelledby="ouv-equipe">
        <div className={pageStyles.sectionTitre}>
          <h2 id="ouv-equipe">{t('ouvertures.fiche.equipe.direction')}</h2>
          {modifiable ? (
            <Link to={`/ouvertures/${dossier.id}/assistant?etape=4`} className="lien-cible">
              {t('ouvertures.fiche.offreContrat.modifier')}
            </Link>
          ) : null}
        </div>
        {equipe.direction ? (
          <dl className={styles.faits}>
            <div className={styles.faitsLigne}>
              <dt>{equipe.direction.nom}</dt>
              <dd>{equipe.direction.email}</dd>
            </div>
          </dl>
        ) : (
          <p className={pageStyles.videEtat}>{t('ouvertures.fiche.equipe.vide')}</p>
        )}
      </section>

      <section className={pageStyles.panneau} aria-labelledby="ouv-membres">
        <div className={pageStyles.sectionTitre}>
          <h2 id="ouv-membres">{t('ouvertures.fiche.equipe.membres')}</h2>
        </div>
        {equipe.membres.length === 0 ? (
          <p className={pageStyles.videEtat}>{t('ouvertures.equipe.aucunMembre')}</p>
        ) : (
          <TableauDefilant libelle={t('ouvertures.fiche.equipe.membres')}>
            <table className={tableStyles.table}>
              <thead>
                <tr>
                  <th scope="col">{t('ouvertures.fiche.equipe.colonnes.nom')}</th>
                  <th scope="col">{t('ouvertures.fiche.equipe.colonnes.email')}</th>
                  <th scope="col">{t('ouvertures.fiche.equipe.colonnes.role')}</th>
                </tr>
              </thead>
              <tbody>
                {equipe.membres.map((membre) => (
                  <tr key={membre.email}>
                    <td className={tableStyles.cellulePrincipale}>{membre.nom}</td>
                    <td>{membre.email}</td>
                    <td>{t(`ouvertures.equipe.roles.${membre.role}`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableauDefilant>
        )}
        <p className={`${styles.groupeTexte} ${styles.espaceHaut}`}>
          {t('ouvertures.fiche.equipe.invitations')} :{' '}
          {t(`ouvertures.equipe.invitationsOptions.${equipe.invitations}`)}
        </p>
      </section>
    </div>
  );
}

/**
 * Reprise : le parcours vit dans la fiche de la galerie, une fois créée. Le
 * connecteur Dropbox, lui, se mène d'ici : il est rattaché au dossier.
 */
export function RepriseDossier({ dossier }: { dossier: DossierOuverture }): JSX.Element {
  const { t } = useTranslation('console');
  return (
    <div className={styles.pile}>
      <section className={pageStyles.panneau} aria-labelledby="ouv-reprise">
        <div className={pageStyles.sectionTitre}>
          <h2 id="ouv-reprise">{t('ouvertures.fiche.reprise.titre')}</h2>
        </div>
        {dossier.galleryId ? (
          <>
            <p>{t('ouvertures.fiche.reprise.texte')}</p>
            <p>
              <Link to={`/galeries/${dossier.galleryId}/reprise`} className="lien-cible">
                {t('ouvertures.fiche.reprise.ouvrir')}
              </Link>
            </p>
          </>
        ) : (
          <p className={pageStyles.videEtat}>{t('ouvertures.fiche.reprise.attente')}</p>
        )}
      </section>
      <ConnecteurDropbox dossier={dossier} />
    </div>
  );
}

/** Journal : celui de la galerie du dossier (le serveur filtre par identifiant). */
export function JournalDossier({ dossier }: { dossier: DossierOuverture }): JSX.Element {
  const { t } = useTranslation('console');
  if (!dossier.galleryId) {
    return <p className={pageStyles.videEtat}>{t('ouvertures.fiche.journal.attente')}</p>;
  }
  return <AuditTable galerie={dossier.galleryId} />;
}
